/**
 * Helpers that take raw rows + the current global data filter
 * (`useDataFilterStore`) and return the filtered subset that the
 * UI should actually display.
 *
 * Both the mock and cloud adapters call these helpers so the rules
 * stay identical regardless of environment.
 */
import { getDataFilter, isInYear } from "./useDataFilterStore";

type AnyClient = { id: string; created_at?: string | null; is_simulated?: boolean | null; status?: string | null };
type AnyProject = { id: string; client_id?: string | null; created_at?: string | null; start_date?: string | null; due_date?: string | null; is_simulated?: boolean | null; status?: string | null };
type AnyInvoice = { id: string; client_id?: string | null; issue_date?: string | null; created_at?: string | null; is_simulated?: boolean | null };
type AnyQuote = { id: string; client_id?: string | null; created_at?: string | null; valid_until?: string | null; is_simulated?: boolean | null; status?: string | null };

function simulatedSet<T extends { id: string; is_simulated?: boolean | null }>(clients: T[]): Set<string> {
  return new Set(clients.filter((c) => !!c.is_simulated).map((c) => c.id));
}

/** Filter the Clients list: hide simulated clients (if off) and clients with no activity in `activeYear`. */
export function applyClientFilter<T extends AnyClient>(
  clients: T[],
  related?: { projects?: AnyProject[]; invoices?: AnyInvoice[]; quotes?: AnyQuote[] },
): T[] {
  const { activeYear, showSimulated, showInactiveClients } = getDataFilter();
  let out = clients;
  if (!showSimulated) out = out.filter((c) => !c.is_simulated);
  if (!showInactiveClients) out = out.filter((c) => c.status !== "inactive");
  if (activeYear !== "all") {
    const projByClient = new Map<string, AnyProject[]>();
    (related?.projects ?? []).forEach((p) => {
      if (!p.client_id) return;
      (projByClient.get(p.client_id) ?? projByClient.set(p.client_id, []).get(p.client_id)!).push(p);
    });
    const invByClient = new Map<string, AnyInvoice[]>();
    (related?.invoices ?? []).forEach((i) => {
      if (!i.client_id) return;
      (invByClient.get(i.client_id) ?? invByClient.set(i.client_id, []).get(i.client_id)!).push(i);
    });
    const qByClient = new Map<string, AnyQuote[]>();
    (related?.quotes ?? []).forEach((q) => {
      if (!q.client_id) return;
      (qByClient.get(q.client_id) ?? qByClient.set(q.client_id, []).get(q.client_id)!).push(q);
    });
    out = out.filter((c) => {
      if (isInYear(c.created_at, activeYear)) return true;
      const hasProj = (projByClient.get(c.id) ?? []).some((p) =>
        isInYear(p.created_at, activeYear) || isInYear(p.start_date, activeYear) || isInYear(p.due_date, activeYear),
      );
      if (hasProj) return true;
      const hasInv = (invByClient.get(c.id) ?? []).some((i) =>
        isInYear(i.issue_date, activeYear) || isInYear(i.created_at, activeYear),
      );
      if (hasInv) return true;
      const hasQ = (qByClient.get(c.id) ?? []).some((q) =>
        isInYear(q.created_at, activeYear) || isInYear(q.valid_until, activeYear),
      );
      return hasQ;
    });
  }
  return out;
}

/** Filter projects by simulated-clients toggle and year (created/start/due). */
export function applyProjectFilter<T extends AnyProject>(rows: T[], allClients: AnyClient[]): T[] {
  const { activeYear, showSimulated, showArchivedProjects } = getDataFilter();
  let out = rows;
  if (!showSimulated) {
    const sim = simulatedSet(allClients);
    out = out.filter((p) => !p.is_simulated && (!p.client_id || !sim.has(p.client_id)));
  }
  if (!showArchivedProjects) out = out.filter((p) => p.status !== "archived");
  if (activeYear !== "all") {
    out = out.filter((p) =>
      isInYear(p.created_at, activeYear) || isInYear(p.start_date, activeYear) || isInYear(p.due_date, activeYear),
    );
  }
  return out;
}

/** Filter invoices by simulated-clients toggle and year (issue_date). */
export function applyInvoiceFilter<T extends AnyInvoice>(rows: T[], allClients: AnyClient[]): T[] {
  const { activeYear, showSimulated } = getDataFilter();
  let out = rows;
  if (!showSimulated) {
    const sim = simulatedSet(allClients);
    out = out.filter((i) => !i.is_simulated && (!i.client_id || !sim.has(i.client_id)));
  }
  if (activeYear !== "all") {
    out = out.filter((i) => isInYear(i.issue_date, activeYear) || isInYear(i.created_at, activeYear));
  }
  return out;
}

/** Filter quotes by simulated-clients toggle and year (created_at / valid_until). */
export function applyQuoteFilter<T extends AnyQuote>(rows: T[], allClients: AnyClient[]): T[] {
  const { activeYear, showSimulated, showExpiredQuotes } = getDataFilter();
  let out = rows;
  if (!showSimulated) {
    const sim = simulatedSet(allClients);
    out = out.filter((q) => !q.is_simulated && (!q.client_id || !sim.has(q.client_id)));
  }
  if (!showExpiredQuotes) out = out.filter((q) => q.status !== "expired");
  if (activeYear !== "all") {
    out = out.filter((q) => isInYear(q.created_at, activeYear) || isInYear(q.valid_until, activeYear));
  }
  return out;
}